import "./inventory.page.css";

import { createItem } from "../../models/Inventory";
import { createInventoryItemFromCatalog } from "../../catalogs/inventoryCatalog";
import { calculateInventoryWeight } from "../../calculations/inventoryWeight";
import React, { useEffect, useMemo, useState } from "react";

import AddItemModal from "./AddItemModal";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import InventoryTable from "./InventoryTable";
import InventoryCatalogModal from "./InventoryCatalogModal";
import PageLoadState from "../../components/layout/PageLoadState/PageLoadState";
import MoneyInput from "./MoneyInput";
import { fetchGet } from "../../clients/response";
import { SERVICE_URL } from "../../clients/app.const";
import { mapJsonToCharacter } from "../../models/Character";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import createId from "../../utils/createId";
import { useCharacterAutoSave } from "../../hooks/useCharacterAutoSave";
import SaveToast from "../../components/layout/SaveToast/SaveToast";

const InventoryPage = () => {
  const { i18n, t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [character, setCharacter] = useState();
  const [loadState, setLoadState] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const { tokenValue, user } = useSelector((s) => s.auth);
  const { dismissSaveMessage, isSaveError, retrySave, saveMessage } =
    useCharacterAutoSave(character, tokenValue);
  const selectedCharacterId = useSelector((s) => s.character.selectedCharacterId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    type: "",
    weight: "",
    price: "",
    notes: "",
    armorClass: "",
    armorType: "armor",
    weaponType: "melee",
    modifier: "",
    range: "",
    ammunitionId: "",
    amount: 1,
    damage: "",
    damageDiceCount: "",
    damageDiceSides: "",
    damageBonus: 0,
    damageType: "",
    crit: "",
    critDiceCount: "",
    critDiceSides: "",
    critBonus: 0,
  });

  const handleAddOrUpdateItem = (itemData) => {
    const items = character.inventory.items;
    const itemIndex = items.findIndex((item) => item.id === itemData.id);

    if (itemIndex > -1) {
      const updatedItem = createItem({ ...itemData });
      const updatedItems = items.map((item, idx) =>
        idx === itemIndex ? updatedItem : item
      );
      setCharacter((prevCharacter) => ({
        ...prevCharacter,
        inventory: {
          ...prevCharacter.inventory,
          items: updatedItems,
        },
      }));
    } else {
      const newItem = createItem({ ...itemData, id: createId() });
      const updatedItems = [...items, newItem];
      setCharacter((prevCharacter) => ({
        ...prevCharacter,
        inventory: {
          ...prevCharacter.inventory,
          items: updatedItems,
        },
      }));
    }
    setIsModalOpen(false);
  };

  const handleCatalogSelect = (catalogItem) => {
    const language = (i18n.resolvedLanguage || i18n.language) === "en" ? "en" : "ru";
    handleAddOrUpdateItem(createInventoryItemFromCatalog(catalogItem, language));
    setIsCatalogOpen(false);
  };

  useEffect(() => {
    let cancelled = false;

    const loadCharacter = async () => {
      if (!selectedCharacterId) {
        setCharacter(undefined);
        setLoadState("empty");
        return;
      }

      setLoadState("loading");
      const response = await fetchGet(
        `${SERVICE_URL}/characters/${selectedCharacterId}`,
        {},
        tokenValue
      );

      if (cancelled) return;
      if (response.isError || !response.data) {
        setCharacter(undefined);
        setLoadState("error");
        return;
      }

      try {
        setCharacter(mapJsonToCharacter(response.data));
        setLoadState("ready");
      } catch (error) {
        console.error("Failed to process character data:", error);
        setCharacter(undefined);
        setLoadState("error");
      }
    };

    loadCharacter();
    return () => {
      cancelled = true;
    };
  }, [selectedCharacterId, tokenValue, user, reloadKey]);
  const totalWeight = useMemo(() => {
    if (!character || !character.inventory) {
      return 0;
    }
    return calculateInventoryWeight(character.inventory.items);
  }, [character]);

  const handleDeleteItem = (itemId) => {
    if (!window.confirm(t("inventory.deleteConfirm"))) {
      return;
    }

    setCharacter((current) => {
      const remainingItems = current.inventory.items
        .filter((item) => item.id !== itemId)
        .map((item) =>
          item.type === "weapon" && item.ammunitionId === itemId
            ? { ...item, ammunitionId: "" }
            : item
        );

      return {
        ...current,
        inventory: {
          ...current.inventory,
          items: remainingItems,
        },
      };
    });
  };
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <CenteredContainer>
      <PageLoadState
        state={loadState}
        message={
          loadState === "loading"
            ? t("inventory.loading")
            : loadState === "empty"
              ? t("inventory.selectCharacter")
              : t("inventory.loadError")
        }
        onRetry={() => setReloadKey((key) => key + 1)}
      />
      {loadState === "ready" && character && (
        <>
          <div className="inventory-form-container">
            <SaveToast
              isError={isSaveError}
              message={saveMessage}
              onDismiss={dismissSaveMessage}
              onRetry={retrySave}
            />
            <div className="inventory-search-container">
              <input
                aria-label={t("inventory.search")}
                type="search"
                placeholder={t("inventory.search")}
                className="inventory-search-input"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <div className="inventory-add-actions">
                <button
                  className="inventory-catalog-open-button"
                  type="button"
                  onClick={() => setIsCatalogOpen(true)}
                >
                  {t("inventory.catalog.open")}
                </button>
                <button
                  aria-label={t("inventory.addItem")}
                  className="add-skill-button"
                  type="button"
                  onClick={() => {
                    setFormData({
                      name: "",
                      type: "",
                      weight: "",
                      price: "",
                      notes: "",
                      armorClass: "",
                      armorType: "armor",
    weaponType: "melee",
    modifier: "",
    range: "",
    ammunitionId: "",
    amount: 1,
    damage: "",
    damageDiceCount: "",
    damageDiceSides: "",
    damageBonus: 0,
    damageType: "",
    crit: "",
    critDiceCount: "",
    critDiceSides: "",
    critBonus: 0,
                    });
                    setIsModalOpen(true);
                  }}
                >
                  +
                </button>

                <AddItemModal
                  isOpen={isModalOpen}
                  onClose={() => setIsModalOpen(false)}
                  onSubmit={handleAddOrUpdateItem}
                  formData={formData}
                  setFormData={setFormData}
                />
                <InventoryCatalogModal
                  isOpen={isCatalogOpen}
                  onClose={() => setIsCatalogOpen(false)}
                  onSelect={handleCatalogSelect}
                />
              </div>
            </div>

            <div className="weight-info">
              <span>{t("inventory.weightCarried", { weight: totalWeight })}</span>
              <span>{t("inventory.maxWeight", { weight: character.inventory.maxWeight })}</span>
            </div>
            <InventoryTable
              character={character}
              searchTerm={searchTerm}
              setFormData={setFormData}
              setIsModalOpen={setIsModalOpen}
              onDeleteItem={handleDeleteItem}
            />
            <MoneyInput character={character} onChange={(money) => setCharacter((current) => ({ ...current, inventory: { ...current.inventory, money } }))} />
          </div>
        </>
      )}
    </CenteredContainer>
  );
};
export default InventoryPage;
